import { LoadingFieldSkeleton } from 'components/secondaryMarket/shared';
import { FeedbackMessage } from 'components/secondaryMarket/shared/FeedbackMessage';
import type { TagItem } from 'components/shared/Tags';
import { SgTagsPicker } from 'components/shared/Tags';
import { FC, useCallback, useMemo } from 'react';
import type { Sales } from 'state/secondaryMarket/shared/referential/referential.model';
import type { LoadingStatus } from 'utils/status';

type SaleSelectorProps = {
  isFrozen: boolean;
  displayErrors?: boolean;
  salesLoadingStatus: LoadingStatus;
  salesId?: string;
  allSales: Sales[];
  onSalesChanged: (salesId: string | undefined) => void;
};

export const SaleSelector: FC<SaleSelectorProps> = ({
  salesLoadingStatus,
  salesId,
  allSales,
  onSalesChanged,
  displayErrors = true,
  isFrozen,
}) => {
  const availableItems = useMemo(() => allSales?.map(s => ({ key: s.id, label: s.firstName }) as TagItem), [allSales]);

  const onSingleSelect = useCallback(
    (selection: readonly string[]) => {
      if (selection?.length === 1) {
        onSalesChanged(selection[0]);
      }
    },
    [onSalesChanged],
  );

  const singleSelection: string[] = useMemo(() => {
    if (salesId) {
      return availableItems?.map(x => x.key)?.filter(x => x === salesId) ?? [];
    }
    return [];
  }, [salesId, availableItems]);

  if (salesLoadingStatus === 'loading') {
    return <LoadingFieldSkeleton />;
  }
  if (salesLoadingStatus === null) {
    return null;
  }

  const hasSelectedMissingSales = !!salesId && singleSelection.length === 0;

  const missing: TagItem = {
    key: salesId!,
    label: `${salesId}`,
    forced: true,
  };

  return (
    <>
      <SgTagsPicker
        id="Sales"
        onSelectionKeysChanged={onSingleSelect}
        singleSelect
        noIcon
        selection={hasSelectedMissingSales ? [missing.key] : singleSelection}
        available={hasSelectedMissingSales ? [...availableItems, missing] : availableItems}
        disabled={isFrozen}
        maxDisplayedItems={availableItems?.length}
        noClear
        placeholder="Sales"
        hasTooltip
      />
      {hasSelectedMissingSales && <FeedbackMessage labelKey="secondaryMarket.errors.sales.incoherent" color="info" />}
      {salesLoadingStatus === 'error' && displayErrors && (
        <FeedbackMessage labelKey="secondaryMarket.errors.salesCoverageLoadFailed" color="danger" />
      )}
    </>
  );
};

SaleSelector.displayName = 'SaleSelector';
